import React, { useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { getStoredUser } from "../components/RegisterModal";
import { Download, Trash2, Shield, AlertTriangle } from "lucide-react";

export default function PrivacySettings() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const user = getStoredUser();
  const email = user?.email;
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState("");

  const exportData = async () => {
    if (!email) return;
    setBusy(true);
    try {
      const res = await api.get(`/users/me/export?email=${encodeURIComponent(email)}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `goldora-export-${email}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert(pt ? "Erro ao exportar dados" : "Failed to export data");
    } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (confirmDel !== email) return;
    setBusy(true);
    try {
      await api.delete(`/users/me?email=${encodeURIComponent(email)}`);
      try { localStorage.removeItem("intelvest_user"); } catch {}
      window.location.href = "/";
    } catch {
      alert(pt ? "Erro ao excluir conta" : "Failed to delete account");
      setBusy(false);
    }
  };

  if (!email) {
    return (
      <div className="max-w-[800px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-privacy-settings">
        <h1 className="font-display text-4xl tracking-tight">{pt ? "Privacidade da conta" : "Account privacy"}</h1>
        <p className="mt-3 text-neutral-400">{pt ? "Faça login para gerenciar seus dados." : "Sign in to manage your data."}</p>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-privacy-settings">
      <span className="text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
        <Shield size={11} className="inline mr-1" />
        {pt ? "Direitos LGPD / GDPR" : "GDPR / Privacy rights"}
      </span>
      <h1 className="mt-2 font-display text-4xl tracking-tight">
        {pt ? "Privacidade da conta" : "Account privacy"}
      </h1>
      <p className="mt-3 text-neutral-400">{pt ? `Logado como ${email}.` : `Signed in as ${email}.`}</p>

      <div className="mt-10 surface p-6">
        <h2 className="font-display text-2xl tracking-tight">
          <Download size={18} className="inline mr-2 text-gold-400" />
          {pt ? "Exportar meus dados" : "Export my data"}
        </h2>
        <p className="mt-2 text-sm text-neutral-300 leading-relaxed">
          {pt
            ? "Baixe um arquivo JSON com tudo que a GOLDORA tem sobre você: perfil, assinatura, watchlist, alertas, avaliações, uso. Direito garantido pela LGPD (Brasil) e GDPR (Europa)."
            : "Download a JSON with everything GOLDORA has on you: profile, subscription, watchlist, alerts, reviews, usage. Right granted by LGPD (Brazil) and GDPR (Europe)."}
        </p>
        <button onClick={exportData} disabled={busy} data-testid="export-data-btn" className="mt-4 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 text-black text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-50">
          <Download size={14} /> {pt ? "Baixar agora (.json)" : "Download now (.json)"}
        </button>
      </div>

      <div className="mt-6 surface p-6 border border-red-500/20">
        <h2 className="font-display text-2xl tracking-tight text-red-300">
          <Trash2 size={18} className="inline mr-2" />
          {pt ? "Excluir minha conta" : "Delete my account"}
        </h2>
        <p className="mt-2 text-sm text-neutral-300 leading-relaxed">
          {pt
            ? "Apaga permanentemente todos os seus dados. Cancela assinaturas. Não há como desfazer."
            : "Permanently deletes all your data and cancels subscriptions. This cannot be undone."}
        </p>
        <div className="mt-4 p-3 bg-red-500/5 border border-red-500/20 rounded-md flex items-start gap-2 text-xs text-red-200">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{pt ? `Para confirmar, digite o email "${email}" abaixo.` : `To confirm, type the email "${email}" below.`}</span>
        </div>
        <input
          value={confirmDel}
          onChange={(e) => setConfirmDel(e.target.value)}
          placeholder={email}
          data-testid="delete-confirm-input"
          className="mt-3 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-red-400"
        />
        <button
          onClick={deleteAccount}
          disabled={busy || confirmDel !== email}
          data-testid="delete-account-btn"
          className="mt-4 inline-flex items-center gap-2 bg-red-500/80 hover:bg-red-500 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Trash2 size={14} /> {pt ? "Excluir permanentemente" : "Delete permanently"}
        </button>
      </div>
    </div>
  );
}
